import { Link } from "react-router-dom";
import {
  Mail, ScrollText, Award, ArrowRight, Building2, Brain, BookOpen,
  Globe, Briefcase
} from "lucide-react";
import ExecutiveHero from "@/components/ExecutiveHero";

const PRESS_HERO =
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=2400&q=80";

const FACT_BLOCKS = [
  { icon: Building2, label: "Organization", value: "Zodiac Workforce Intelligence Institute (ZWII)" },
  { icon: Award,     label: "Founded",      value: "2026 · Founder & President: Dr. Spencer W. Charles, PhD" },
  { icon: Brain,     label: "Doctrine",     value: "WAIT Theory — Workforce Alignment Intelligence Theory" },
  { icon: BookOpen,  label: "Publication",  value: "SCFR — Society & Credential Fatigue Review (peer-reviewed)" },
  { icon: Globe,     label: "Scope",        value: "Workforce intelligence research, education, and instrumentation" },
];

export default function Press() {
  return (
    <div className="min-h-screen zodiac-page" data-testid="press-page">
      <ExecutiveHero
        eyebrow="Press & Media Center"
        title={<>Media inquiries &</>}
        accentTitle="institutional information."
        subtitle="A centralized resource for journalists, partners, and media. Below: institutional overview, founder summary, doctrine summary, and direct media contact."
        image={PRESS_HERO}
        breadcrumb={[{ label: "Home", href: "/" }, { label: "Press" }]}
        actions={[
          { label: "Media Kit", href: "/media-kit", testId: "press-media-kit-cta" },
          { label: "Contact Press Office", href: "/contact", variant: "ghost" },
        ]}
      />

      {/* About ZWII */}
      <section className="bg-white py-20" data-testid="press-about">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
            <span className="w-8 h-px bg-[#1E3A8A]" /> About ZWII
          </div>
          <h2 className="zodiac-heading text-3xl sm:text-4xl font-bold text-[#0F1E47] mb-5 leading-tight">
            The Zodiac Workforce Intelligence <span className="italic text-[#991B1B] font-normal">Institute.</span>
          </h2>
          <p className="text-[#475569] leading-relaxed text-lg mb-6">
            ZWII is a premium institutional ecosystem dedicated to the scientific advancement of
            workforce intelligence in the Human-AI era. Through original doctrine, peer-reviewed
            research, executive programs, and global instrumentation, the Institute pioneers a
            measurable institutional discipline for workforce alignment.
          </p>
          <div className="p-6 rounded-xl bg-[#FBFAF7] border-l-4 border-[#C8A157]">
            <p className="text-[10px] uppercase tracking-widest text-[#1E3A8A] font-semibold mb-2">Mission Statement</p>
            <p className="text-[#0F1E47] text-lg italic leading-relaxed">
              "To transform institutional capacity from credential-based assumptions to
              intelligence-based measurement."
            </p>
          </div>
        </div>
      </section>

      {/* Institutional Overview - Fact Sheet */}
      <section className="bg-[#FBFAF7] border-y border-[#E5E7EB] py-20" data-testid="press-fact-sheet">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
            <span className="w-8 h-px bg-[#C8A157]" /> Institutional Fact Sheet
          </div>
          <h2 className="zodiac-heading text-3xl font-bold text-[#0F1E47] mb-8">At a glance.</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {FACT_BLOCKS.map((f) => (
              <div key={f.label} className="flex items-start gap-4 p-5 rounded-lg bg-white border border-[#E5E7EB]">
                <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/[0.08] flex items-center justify-center flex-shrink-0">
                  <f.icon className="w-5 h-5 text-[#1E3A8A]" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-[#6B7280] font-semibold mb-0.5">{f.label}</p>
                  <p className="text-[#0F1E47] font-medium">{f.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Summary */}
      <section className="bg-white py-20" data-testid="press-founder">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-[280px_1fr] gap-8 items-start">
          <div className="rounded-2xl overflow-hidden shadow-[0_12px_40px_rgba(15,30,71,0.12)] ring-1 ring-[#0F1E47]/5">
            <img
              src="https://customer-assets.emergentagent.com/job_88d281f7-bb96-4c9e-b96f-7ca8aaa7f9b2/artifacts/1ggn816l_Profile%20Picture.png"
              alt="Dr. Spencer W. Charles"
              className="w-full aspect-square object-cover object-top"
            />
          </div>
          <div>
            <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
              <span className="w-8 h-px bg-[#991B1B]" /> Founder Biography
            </div>
            <h2 className="zodiac-heading text-2xl sm:text-3xl font-bold text-[#0F1E47] mb-3 leading-tight">
              Dr. Spencer W. Charles, <span className="italic text-[#991B1B] font-normal">PhD</span>
            </h2>
            <p className="text-[#6B7280] text-sm uppercase tracking-widest font-semibold mb-4">Founder · President · Editor-in-Chief, SCFR</p>
            <p className="text-[#475569] leading-relaxed mb-5">
              Dr. Charles is the architect of WAIT Theory and the principal scholar shaping the
              Institute's doctrinal program. He holds a PhD in Leadership Studies from Our Lady
              of the Lake University and operates at the convergence of workforce development,
              leadership science, quality management, and AI integration.
            </p>
            <Link to="/founder">
              <button className="h-11 px-5 rounded-md bg-[#0F1E47] text-white hover:bg-[#1E3A8A] font-semibold inline-flex items-center gap-2">
                Full Founder Profile <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* WAIT Theory Summary */}
      <section className="bg-[#FBFAF7] border-y border-[#E5E7EB] py-20" data-testid="press-wait">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
            <span className="w-8 h-px bg-[#1E3A8A]" /> WAIT Theory Summary
          </div>
          <h2 className="zodiac-heading text-3xl font-bold text-[#0F1E47] mb-5 leading-tight">
            The Institute's <span className="italic text-[#991B1B] font-normal">foundational doctrine.</span>
          </h2>
          <p className="text-[#475569] leading-relaxed mb-6">
            <strong className="text-[#0F1E47]">WAIT Theory</strong> — Workforce Alignment Intelligence Theory — defines workforce
            dysfunction not as a skills problem but as a structural alignment intelligence
            failure. Its constitutional framework includes the Five-Layer Architecture, the
            Workforce Intelligence Index (WII), and the Seven Forces of Workforce Alignment.
          </p>
          <Link to="/wait-theory">
            <button className="h-11 px-5 rounded-md bg-[#1E3A8A] text-white hover:bg-[#15307A] font-semibold inline-flex items-center gap-2">
              Explore WAIT Theory <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>

      {/* SCFR Summary */}
      <section className="bg-white py-20" data-testid="press-scfr">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
            <span className="w-8 h-px bg-[#991B1B]" /> SCFR Summary
          </div>
          <h2 className="zodiac-heading text-3xl font-bold text-[#0F1E47] mb-5 leading-tight">
            Society & Credential Fatigue <span className="italic text-[#991B1B] font-normal">Review.</span>
          </h2>
          <p className="text-[#475569] leading-relaxed mb-6">
            ZWII's flagship peer-reviewed journal — convening doctrinal scholarship and empirical
            research on workforce alignment, credential reform, and institutional transformation.
            Featured edition: <em className="text-[#0F1E47]">WAIT Theory 2.0 — Constitutional Authority Edition (2026)</em>.
          </p>
          <Link to="/scfr-journal">
            <button className="h-11 px-5 rounded-md bg-[#991B1B] text-white hover:bg-[#7F1D1D] font-semibold inline-flex items-center gap-2">
              Browse SCFR-Journal <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>

      {/* Media Contact + Partnership */}
      <section className="bg-[#0F1E47] text-white py-20 relative overflow-hidden" data-testid="press-contact">
        <div className="absolute -top-32 -left-20 w-[420px] h-[420px] rounded-full bg-[#C8A157]/10 blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          <div className="p-7 rounded-2xl bg-white/5 backdrop-blur border border-white/10">
            <Mail className="w-7 h-7 text-[#C8A157] mb-4" />
            <h3 className="zodiac-heading text-2xl font-bold mb-3">Media Contact</h3>
            <p className="text-white/70 leading-relaxed mb-5">
              For interviews, expert commentary, doctrinal questions, or research citations —
              contact the Office of the President.
            </p>
            <Link to="/contact">
              <button className="h-11 px-5 rounded-md bg-white text-[#0F1E47] hover:bg-[#FBFAF7] font-semibold inline-flex items-center gap-2">
                Contact Press Office <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
          <div className="p-7 rounded-2xl bg-[#C8A157]/10 backdrop-blur border border-[#C8A157]/30">
            <Briefcase className="w-7 h-7 text-[#C8A157] mb-4" />
            <h3 className="zodiac-heading text-2xl font-bold mb-3">Partnership Inquiries</h3>
            <p className="text-white/70 leading-relaxed mb-5">
              Academic, government, corporate, and global partnership pathways — initiate an
              institutional conversation.
            </p>
            <Link to="/contact">
              <button className="h-11 px-5 rounded-md bg-[#C8A157] text-[#0F1E47] hover:bg-[#E5C275] font-semibold inline-flex items-center gap-2">
                Open Partnership Channel <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
